import pino from "pino";
import { trace } from "@opentelemetry/api";
import { getPublicEnv, getPublicNodeEnv } from "../config/public-env";

const SENSITIVE_LOG_KEY =
  /(authorization|cookie|password|secret|token|email|phone|address|nhs.?number|date.?of.?birth|dob|nino)/i;
const MAX_BROWSER_LOG_LENGTH = 8_000;

export const sanitizeLogValue = (
  value: unknown,
  seen = new WeakSet<object>()
): unknown => {
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      code: (value as any).code ?? null,
      status: (value as any).status ?? (value as any).response?.status ?? null,
    };
  }
  if (value === null || typeof value !== "object") return value;
  if (seen.has(value)) return "[Circular]";
  seen.add(value);
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeLogValue(item, seen));
  }
  return Object.entries(value).reduce<Record<string, unknown>>(
    (result, [key, item]) => {
      if (!SENSITIVE_LOG_KEY.test(key)) {
        result[key] = sanitizeLogValue(item, seen);
      }
      return result;
    },
    {}
  );
};

export const serializeLogMessage = (messages: unknown[]) =>
  messages
    .map((message) => {
      if (typeof message === "string") return message;
      try {
        return JSON.stringify(sanitizeLogValue(message, new WeakSet()));
      } catch {
        return "[Unserializable log message]";
      }
    })
    .join(" ");

function createBrowserLogRequestId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `browser-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function activeTraceFields() {
  const context = trace.getActiveSpan()?.spanContext();
  return context ? { traceId: context.traceId, spanId: context.spanId } : {};
}

// Factory function for testability
export function createLogger() {
  const isBrowser = typeof window !== "undefined";
  const browserLevel = getPublicEnv("NEXT_PUBLIC_LOG_LEVEL", "info");
  // Never couple the CloudWatch/server threshold to a public runtime value.
  // Browser telemetry must remain visible even when client logging is reduced.
  const serverLevel = process.env.LOG_LEVEL || "info";
  const nodeEnv = getPublicNodeEnv();

  if (isBrowser) {
    const isLocal = nodeEnv === "local" || nodeEnv === "development";

    return pino({
      level: browserLevel,
      browser: {
        // Always forward browser logs to the Next.js /api/logs route so they
        // appear in the terminal running `yarn start`/`yarn dev` alongside
        // server logs.  In local dev we forward from `debug` so you see
        // everything in one place; in deployed envs we forward `info`+
        // to keep CloudWatch noise low.
        transmit: {
          level: isLocal ? "debug" : "info",
          send(_level, logEvent) {
            const bindings = (logEvent.bindings || []).reduce(
              (acc: Record<string, unknown>, b: Record<string, unknown>) => ({
                ...acc,
                ...b,
              }),
              {} as Record<string, unknown>
            );
            fetch("/api/logs", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "X-Wisterias-Browser-Log": "1",
                "X-Request-Id": createBrowserLogRequestId(),
              },
              body: JSON.stringify({
                level: _level,
                msg: serializeLogMessage(logEvent.messages || []).slice(
                  0,
                  MAX_BROWSER_LOG_LENGTH
                ),
                time: logEvent.ts,
                source: "browser",
                bindings: sanitizeLogValue(bindings),
              }),
              keepalive: true,
            }).catch(() => {
              /* fire-and-forget — never throw in a log transmit */
            });

            // Faro owns browser errors, performance, replay and tracing. Mirror
            // explicit application logs too, without making logger startup pull
            // the Faro SDK into every bundle.
            void import("./faro")
              .then(({ pushFaroLog }) =>
                pushFaroLog(
                  _level,
                  serializeLogMessage(logEvent.messages || []).slice(
                    0,
                    MAX_BROWSER_LOG_LENGTH
                  ),
                  bindings
                )
              )
              .catch(() => {
                /* observability must never affect user interaction */
              });
          },
        },
      },
    });
  }

  return pino({
    level: serverLevel,
    redact: {
      paths: [
        "authorization",
        "cookie",
        "password",
        "secret",
        "token",
        "*.authorization",
        "*.cookie",
        "*.password",
        "*.secret",
        "*.token",
      ],
      remove: true,
    },
    mixin: activeTraceFields,
    formatters: {
      level(label) {
        return { level: label };
      },
    },
    timestamp: pino.stdTimeFunctions.isoTime,
  });
}

// Default logger instance for app usage
const logger = createLogger() as any;
export default logger;
